import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const { forgotPassword } = useAuth();
  const navigate = useNavigate();

  const renderTurnstile = useCallback(() => {
    if (turnstileRef.current && window.turnstile && turnstileWidgetId.current === null) {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        size: 'normal',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => {
          setTurnstileToken('');
          setError('Human verification failed. Please refresh the page and try again.');
        },
        'timeout-callback': () => {
          setTurnstileToken('');
          setError('Human verification timed out. Please refresh the page and try again.');
        },
      });
    }
  }, [turnstileSiteKey]);

  useEffect(() => {
    if (!turnstileSiteKey) return;

    if (window.turnstile) {
      renderTurnstile();
      return;
    }
    const interval = setInterval(() => {
      if (window.turnstile) {
        renderTurnstile();
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [turnstileSiteKey, renderTurnstile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (turnstileSiteKey && !turnstileToken) {
      setError('Please complete the human verification check.');
      setLoading(false);
      return;
    }

    try {
      const result = turnstileToken
        ? await forgotPassword(email, turnstileToken)
        : await forgotPassword(email);
      setSuccess(result.message);

      if (result.user_id) {
        setTimeout(() => {
          navigate('/reset-password', {
            state: {
              userId: result.user_id,
              email: result.email
            }
          });
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a code to reset your password.
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {turnstileSiteKey && <div ref={turnstileRef} />}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </div>

          <div className="text-center">
            <Link 
              to="/login" 
              className="text-sm hover:underline"
              style={{ color: '#615DEC' }}
              onMouseEnter={(e) => {
                e.target.style.color = '#4c47c7';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#615DEC';
              }}
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;